import { SpaceFilterPills, useSpaceFilteredRows } from '@/app/equipmentTypes/SpaceFilterPills';
import { InfoBadge } from '@/components/badges/Common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { formatDate } from '@/util/dates';
import type { EquipmentTypeCandidate } from '@rock/types';
import type { FC } from 'react';

/**
 * Equipment people added as custom types, grouped across homes — what the catalog is missing,
 * ranked by how many homes asked for it.
 */
export const CandidatesTab: FC<{ candidates: EquipmentTypeCandidate[]; loaded: boolean }> = ({
    candidates,
    loaded,
}) => {
    const visibleCandidates = useSpaceFilteredRows(candidates);

    return (
        <>
            <SpaceFilterPills rows={candidates} />
            {loaded ? (
                <CandidatesTable candidates={visibleCandidates} />
            ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            )}
        </>
    );
};

const CandidatesTable: FC<{ candidates: EquipmentTypeCandidate[] }> = ({ candidates }) => {
    const onRowClick = useRowClick();

    if (candidates.length === 0) {
        return (
            <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                No candidates here yet.
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Space</TableHead>
                    <TableHead className="text-right">Homes</TableHead>
                    <TableHead>Promotion</TableHead>
                    <TableHead>AI Classification</TableHead>
                    <TableHead>First Seen</TableHead>
                    <TableHead>Last Seen</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {candidates.map((candidate) => (
                    <TableRow
                        key={candidate.id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={onRowClick(`/equipment/candidates/${candidate.id}`)}
                    >
                        <TableCell className="font-medium">{candidate.title}</TableCell>
                        <TableCell>
                            <InfoBadge>{candidate.spaceId}</InfoBadge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{candidate.count}</TableCell>
                        <TableCell>
                            <PromotionCell candidate={candidate} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">{candidate.aiClassification}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(candidate.firstSeenAt)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(candidate.lastSeenAt)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

/**
 * How far along this candidate's promotion is, at a glance. "Linked" covers both a still-draft type
 * and a live one that hasn't absorbed every custom yet — the detail page (which reads the type's own
 * draft status) is where that distinction lives.
 */
const PromotionCell: FC<{ candidate: EquipmentTypeCandidate }> = ({ candidate }) => {
    if (candidate.promotedTo) return <InfoBadge>Served by {candidate.promotedTo}</InfoBadge>;
    if (candidate.promotionTypeId) return <InfoBadge>Linked to {candidate.promotionTypeId}</InfoBadge>;
    return <span className="text-muted-foreground">—</span>;
};
